import { useEffect, useMemo, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useLoaderData } from 'react-router';
import { ArrowUpRight, Maximize2 } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import getGallerySlides from '@modules/product/util/get-gallery-slides';
import type { loader } from '~/routes/_app.product.$urlSegment';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    CONTENT_CAP_PADDED,
    DetailSaveButton,
    TrackedWebsiteCta,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { useProductEnquiryModal } from '../hooks/use-product-enquiry-modal';
import {
    DetailImageOverlay,
    type OverlaySlide,
} from '~/ui-rocco/pages/detail-shared/DetailImageOverlay';
import { useProductLinkTracking } from '../hooks/use-product-link-tracking';
import { ProductSpecList } from './spec-accordion/ProductSpecList';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useProductDetailItems } from './spec-accordion/use-product-detail-items';
import {
    PRODUCT_DETAILS_SECTION_ID,
    productDetailsExpandedAtom,
} from '../state/details-expanded-atom';

/**
 * The controls that ride on the frame: a white pill bottom-left and a white
 * disc bottom-right, both lifted off the image by a soft shadow so they hold
 * against a light clear-cut as well as a dark photograph.
 */
/**
 * Quiet until the reader is on the image, then all of them at once.
 *
 * A named group: the expand button carries its own `group` for its label, and
 * an unnamed one here would capture that too.
 */
const FRAME_REVEAL = cn(
    'opacity-0 transition-opacity duration-200',
    'group-hover/frame:opacity-100 focus-visible:opacity-100',
    'motion-reduce:transition-none',
);

/**
 * The two actions in the top corner are platform buttons, the same lifted
 * white pill the purchase block's Enquire is, so the frame's controls and the
 * page's read as one family. Only the reveal and the click-through are added.
 */
const FRAME_ACTION = detailCta(
    'white-lifted',
    cn(FRAME_REVEAL, 'pointer-events-auto'),
);

const FRAME_CONTROL = cn(
    'pointer-events-auto inline-flex items-center bg-white text-[#181818] no-underline',
    'font-sans font-medium transition-colors hover:bg-white/90',
    '[box-shadow:0_2px_8px_0_rgba(0,0,0,0.08),0_8px_24px_0_rgba(0,0,0,0.06)]',
);

/** The frame is 41.95% of the page, the project's About picture. */
const FRAME_SIZES = getImagesSizes({ desktop: 806, mobile: 414 });

interface FrameImage {
    src: string;
    alt: string;
    /** A drawing or a cut-out has to be seen whole; a photograph can crop. */
    contain: boolean;
}

/**
 * What the frame shows, in the order the product can supply it: the dimension
 * diagram first, since it is the one image drawn for this purpose; then a
 * clear-cut of the product, which reads as an object rather than a scene; and
 * failing both, the product's main photograph.
 */
const useFrameImage = (): FrameImage | undefined => {
    const { product } = useLoaderData<typeof loader>();
    const { selectedVariant } = useVariantSelectionState();

    // A chosen variant is the reader asking to see that one. Its `Image` is a
    // `LibraryImage` like the product's own, so the crop comes off the image
    // list the page already holds rather than from another query — the variant
    // itself carries only the ID.
    const variantImageId = selectedVariant?.Image?.ID;
    if (variantImageId) {
        const match = (product.LibraryImages ?? []).find(
            (image) => image?.ID === variantImageId,
        );
        // Natural first, then the square crop. The drawer reads the square
        // one for variant images because that is the crop these always carry;
        // natural is not generated for every upload.
        const variantSrc =
            match?.LibraryImageFile?.DetailPageNatural?.Desktop ||
            match?.LibraryImageFile?.DetailPageSquare?.Desktop;
        if (variantSrc) {
            return {
                src: variantSrc,
                alt: product.Title,
                contain: Boolean(match?.IsClearCut),
            };
        }
    }

    const diagram = (product.DiagramImages ?? [])[0]?.LibraryImageFile
        ?.DetailPageNatural?.Desktop;
    if (diagram) {
        return {
            src: diagram,
            alt: `${product.Title} dimensions`,
            contain: true,
        };
    }

    const images = product.LibraryImages ?? [];
    const clearCut = images.find((image) => image?.IsClearCut);
    const clearCutSrc = clearCut?.LibraryImageFile?.DetailPageNatural?.Desktop;
    if (clearCutSrc) {
        return { src: clearCutSrc, alt: product.Title, contain: true };
    }

    // DefaultImage is carried with only the square crop, so the natural one
    // comes off the image list when there is a match for it there.
    const main =
        images[0]?.LibraryImageFile?.DetailPageNatural?.Desktop ??
        product.DefaultImage?.LibraryImageFile?.DetailPageSquare?.Desktop;
    return main ? { src: main, alt: product.Title, contain: false } : undefined;
};

/**
 * Downloads + Details — half the page given to one image, the spec list beside
 * it on the right.
 *
 * A page section rather than an accordion row: it carries no number and no
 * hairline of its own, and everything below it sits that much higher.
 */
export const ProductDetailsSection = () => {
    const { openEnquiryModal } = useProductEnquiryModal();
    const { product, primaryCTA } = useLoaderData<typeof loader>();
    const enquireLabel = primaryCTA.primaryCTALabelTitleCase || 'Enquire Now';
    const trackContentLink = useProductLinkTracking();
    const { items, descriptionHtml, scopeOfUseHtml, customSections, isEmpty } =
        useProductDetailItems();
    // Every product opens on the column's crop: navigating between two
    // products keeps the same route, and the atom outlives the first one.
    const setExpanded = useSetAtom(productDetailsExpandedAtom);
    useEffect(() => setExpanded(false), [product.ID, setExpanded]);
    const image = useFrameImage();
    const [viewerOpen, setViewerOpen] = useState(false);
    // Where the frame sat when the reader clicked it, so the overlay can open
    // out of it rather than appearing at full size.
    const frameRef = useRef<HTMLDivElement>(null);
    const originRect = useRef<DOMRect | null>(null);

    const website = product.Website || product.Professional.Website;

    // The diagram belongs in this stack even though the gallery leaves it out:
    // the frame prefers to show one, so expanding it has to be able to.
    // Videos and anything without a full-size file drop out — the overlay
    // shows photographs.
    const slides = useMemo<OverlaySlide[]>(
        () =>
            getGallerySlides({
                LibraryImages: product.LibraryImages ?? [],
                LibraryVideos: [],
                DiagramImages: product.DiagramImages ?? [],
            })
                .map((slide) =>
                    'LibraryImageFile' in slide
                        ? {
                              src:
                                  slide.LibraryImageFile?.DetailPageNatural
                                      ?.Desktop ?? '',
                              alt: product.Title,
                              // So a save from the viewer pins this picture
                              // rather than the product behind it.
                              id: slide.ID,
                          }
                        : { src: '', alt: product.Title },
                )
                .filter((slide) => slide.src),
        [product.LibraryImages, product.DiagramImages, product.Title],
    );

    // Open on whatever the frame is showing rather than at the top of the
    // stack, so expanding enlarges that image and not a different one.
    const [overlayIndex, setOverlayIndex] = useState(0);
    const frameIndex = Math.max(
        0,
        slides.findIndex((slide) => slide.src === image?.src),
    );

    /**
     * Open the viewer on whatever the frame is showing. Shared by the frame
     * itself and the expand button so the two cannot drift — the button is the
     * affordance, the frame is what a reader actually clicks.
     */
    const openViewer = () => {
        originRect.current = frameRef.current?.getBoundingClientRect() ?? null;
        setOverlayIndex(frameIndex);
        setViewerOpen(true);
    };

    if (isEmpty) return null;

    return (
        // 130px between the statement line above and the image.
        <section
            // The statement's Read more lands here, below the sticky nav.
            id={PRODUCT_DETAILS_SECTION_ID}
            className="scroll-mt-24 bg-ground max-md:pt-12 md:pt-[8.125rem]"
        >
            {/* The project page's About, to the measure: the picture on
                41.95% of the content width, 100px to the copy, and the copy
                on 49.6% less that gap, stopping 8.45% short of the edge. */}
            <div className="w-full md:px-[3rem]">
                <div
                    className={cn(
                        CONTENT_CAP_PADDED,
                        'md:flex md:gap-[6.25rem]',
                    )}
                >
                    {/* Squared off on the page's 32px card corner.

                    The cell stays stretched to the row's full height and the
                    frame sticks inside it — a cell sized to its own content
                    gives a sticky child no room to travel, so it would never
                    move. It holds 48px off the top, the page's own gutter,
                    and stops when the row's height runs out. */}
                    <div className="max-md:px-6 md:w-[41.95%] md:shrink-0">
                        <div
                            ref={frameRef}
                            className={cn(
                                'group/frame relative aspect-square w-full overflow-hidden rounded-[2rem]',
                                'md:sticky md:top-[3rem]',
                                image?.contain ? 'bg-white' : 'bg-ground',
                            )}
                        >
                            {image ? (
                                <Image
                                    src={image.src}
                                    alt={image.alt}
                                    fill
                                    fit={image.contain ? 'contain' : 'cover'}
                                    sizes={FRAME_SIZES}
                                    fadeIn={false}
                                />
                            ) : null}

                            {/* The picture opens the viewer, as a control of
                            its own beneath the two on top of it. A reader who
                            wants a photograph bigger clicks the photograph;
                            the expand button is what tells them they can, not
                            the only way to do it.

                            Sits under the control layer, which passes clicks
                            through everywhere except on the two buttons, so
                            Visit site and View larger keep their own actions. */}
                            {slides.length > 0 ? (
                                <button
                                    type="button"
                                    aria-label={`View ${product.Title} larger`}
                                    onClick={openViewer}
                                    className="absolute inset-0 z-0 cursor-pointer bg-transparent p-0"
                                />
                            ) : null}

                            {/* Top right: what to do about this product, as
                            against the bottom row's what to do with the
                            picture. Same reveal, so the frame's controls all
                            arrive together. */}
                            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-end gap-[0.5rem] max-md:p-4 md:p-[1.5rem]">
                                <button
                                    type="button"
                                    onClick={() => openEnquiryModal()}
                                    className={FRAME_ACTION}
                                >
                                    {enquireLabel}
                                </button>

                                <DetailSaveButton
                                    pinId={product.ID}
                                    pinType="ProductPin"
                                    label="Save this variant"
                                    className={FRAME_ACTION}
                                />
                            </div>

                            {/* The layer itself takes no clicks, so the image
                            underneath is still the whole of the frame; only
                            the two controls on it do. */}
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex items-end justify-between max-md:p-4 md:p-[1.5rem]">
                                <TrackedWebsiteCta
                                    asLink
                                    website={website}
                                    libraryItemID={product.ID}
                                    professionalID={product.Professional.ID}
                                    professionalTitle={
                                        product.Professional.Title
                                    }
                                    source="ProductFrameWebsite"
                                    className={cn(
                                        FRAME_CONTROL,
                                        // The M step (44px), the tile buttons' own
                                        // height, so the frame's pair sits on the
                                        // ladder rather than between its rungs.
                                        'gap-[0.5rem] rounded-[var(--radius-btn-l)] max-md:rounded-[var(--radius-btn-m)] max-md:px-4 max-md:py-2.5 max-md:text-sm md:h-11 md:px-[1.25rem] md:text-[1rem]',
                                        FRAME_REVEAL,
                                    )}
                                >
                                    <ArrowUpRight
                                        aria-hidden="true"
                                        className="shrink-0 max-md:size-4 md:size-[1.125rem]"
                                    />
                                    Visit site
                                </TrackedWebsiteCta>

                                {slides.length > 0 ? (
                                    <button
                                        type="button"
                                        aria-label="View larger"
                                        onClick={openViewer}
                                        className={cn(
                                            FRAME_CONTROL,
                                            'group ml-auto cursor-pointer justify-center rounded-[1.375rem] whitespace-nowrap',
                                            // A square at rest, and only as wide as
                                            // its padding plus the icon. The label
                                            // beside it is what makes it grow.
                                            // The M step at every width, matching
                                            // the Visit site pill beside it.
                                            'max-md:h-11 max-md:rounded-[var(--radius-btn-m)] max-md:px-[0.8125rem] md:h-11 md:px-[1.0625rem]',
                                        )}
                                    >
                                        {/* Collapsed to nothing until hover, then
                                        opened leftwards — the button is pinned
                                        to the frame's right edge, so all its
                                        growth happens on the left.

                                        `max-width` rather than the 0fr/1fr
                                        grid trick used elsewhere: that sizes
                                        rows, and inside a shrink-to-fit flex
                                        item an fr track contributes no width
                                        at all, so the button never grew.

                                        The cap is set just past the label's
                                        own width on purpose. Sweeping to a
                                        loose cap spends most of the duration
                                        past the point the text is fully out,
                                        so opening looked instant while
                                        closing looked smooth. */}
                                        <span
                                            className={cn(
                                                'block max-w-0 overflow-hidden opacity-0 transition-all duration-200 ease-out',
                                                'group-hover:max-w-[6.25rem] group-hover:opacity-100',
                                                'group-focus-visible:max-w-[6.25rem] group-focus-visible:opacity-100',
                                                'motion-reduce:transition-none',
                                            )}
                                        >
                                            <span className="block pr-[0.5rem] max-md:text-sm md:text-[1rem]">
                                                View larger
                                            </span>
                                        </span>
                                        <Maximize2 className="shrink-0 max-md:size-4 md:size-[1.125rem]" />
                                    </button>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {/* The copy, level with the picture's top edge: the same
                    section, drawn on both pages at the same measure. */}
                    <div className="w-full max-md:px-6 md:mr-[8.45%] md:w-[calc(49.6%-6.25rem)]">
                        <div className="w-full max-md:py-8 md:pb-[5rem]">
                            {/* The description opens the column, as the
                            project's opens its About: the statement at the
                            top of the page is its first lines, and its Read
                            more comes down here for the rest. Keyed to the
                            product so every page starts on its own crop. */}
                            <ProductSpecList
                                key={product.ID}
                                items={items}
                                onLinkClick={trackContentLink}
                                descriptionHtml={descriptionHtml}
                                scopeOfUseHtml={scopeOfUseHtml}
                                customSections={customSections}
                                fitToRef={frameRef}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {viewerOpen ? (
                <DetailImageOverlay
                    slides={slides}
                    originRect={originRect.current}
                    index={overlayIndex}
                    onIndexChange={setOverlayIndex}
                    onClose={() => setViewerOpen(false)}
                    pinId={product.ID}
                    title={product.Title}
                    visitWebsite={
                        website
                            ? {
                                  website,
                                  libraryItemID: product.ID,
                                  professionalID: product.Professional.ID,
                                  professionalTitle: product.Professional.Title,
                              }
                            : null
                    }
                />
            ) : null}
        </section>
    );
};
